// src/pages/pmstudio/components/RichTextEditor.jsx
import React, { useCallback, useMemo, useState } from "react";
import { createEditor, Node } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { withHistory } from "slate-history";

/* ------------ helpers: plain string <-> Slate value ------------------- */

function plainToSlate(text) {
  if (!text || !text.trim()) {
    return [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ];
  }
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((p) => ({
    type: "paragraph",
    children: [{ text: p }],
  }));
}

/**
 * Enkel rich text-editor utan toolbar.
 *
 * Props:
 *  - initialText: plain string från backend (t.ex. pm.sections[id])
 *  - onChangeText: (plainText) => void  – kallas på varje ändring
 *  - placeholder?: string
 */
export default function RichTextEditor({
  initialText,
  onChangeText,
  placeholder = "Skriv IM-text här…",
}) {
  const editor = useMemo(
    () => withHistory(withReact(createEditor())),
    []
  );

  const [value, setValue] = useState(() => plainToSlate(initialText));

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const handleChange = (newValue) => {
    setValue(newValue);

    // platta ner Slate-value till plain text – detta är texten som sparas i PM
    const plain = newValue.map((n) => Node.string(n)).join("\n\n");
    if (typeof onChangeText === "function") {
      onChangeText(plain);
    }
  };

  return (
    <Slate editor={editor} initialValue={value} onChange={handleChange}>
      {/* Ingen toolbar här */}
      <Editable
        className="pmstudio-rt-editable"
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        spellCheck
        autoCorrect="on"
      />
    </Slate>
  );
}

/* ------------ Element + Leaf rendering -------------------------------- */

const Element = ({ attributes, children, element }) => {
  switch (element.type) {
    case "paragraph":
    default:
      return (
        <p {...attributes} className="pmstudio-rt-p">
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children }) => {
  // inga marks (bold/italic osv) – ren text
  return (
    <span {...attributes} className="pmstudio-rt-leaf">
      {children}
    </span>
  );
};
